import React, { useState, useEffect, useRef } from 'react';
import { getApiUrl } from '../../apiConfig';

const STORAGE_KEY = 'crops_paddy_data_public';

export default function CropsManage({ onSubmitting }) {
    const [cropsData, setCropsData] = useState(() => {
        const local = localStorage.getItem(STORAGE_KEY);
        try {
            return local ? JSON.parse(local) : [];
        } catch (e) {
            return [];
        }
    });
    const [loading] = useState(false);
    const [form, setForm] = useState({
        year: '',
        area: '',
        production: ''
    });
    const [message, setMessage] = useState('');

    // Fetch data from server on load
    const loadingRef = useRef(false);
    loadingRef.current = loading;

    useEffect(() => {
        let isCancelled = false;
        
        const fetchCropsData = async () => {
            // Use ref for latest loading state
            if (loadingRef.current) return;

            try {
                const res = await fetch(getApiUrl(`api_crops.php?category=paddy&t=${Date.now()}`), {
                    headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
                });
                if (isCancelled) return;
                
                if (res.ok) {
                    const text = await res.text();
                    try {
                        const data = JSON.parse(text);
                        if (Array.isArray(data)) {
                            setCropsData(currentData => {
                                const currentDataStr = JSON.stringify(currentData);
                                const newDataStr = JSON.stringify(data);

                                if (newDataStr !== currentDataStr) {
                                    console.log('🔄 Admin Paddy data synced from server');
                                    localStorage.setItem(STORAGE_KEY, newDataStr);
                                    return data;
                                }
                                return currentData;
                            });
                        }
                    } catch (parseErr) {
                        console.warn('Server response is not JSON:', parseErr);
                    }
                }
            } catch (err) {
                console.warn('Server fetch failed, falling back to local storage', err);
            }
        };

        // Initial fetch
        fetchCropsData();

        return () => {
            isCancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = async (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        if (!form.year || !form.area || !form.production) {
            setMessage('⚠ කරුණාකර සියලු තොරතුරු ඇතුළත් කරන්න.');
            setTimeout(() => setMessage(''), 3000);
            return;
        }
        
        console.log('📝 [WEE] Smart-Sync Submit Started');
        
        if (onSubmitting) onSubmitting(true);
        setMessage('⏳ Syncing...');

        try {
            // 1. Fetch latest server data FIRST
            let serverData = [];
            try {
                const res = await fetch(getApiUrl(`api_crops.php?category=paddy&t=${Date.now()}`), {
                    headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
                });
                if (res.ok) {
                    const text = await res.text();
                    try {
                        const data = JSON.parse(text);
                        if (Array.isArray(data)) serverData = data;
                    } catch (parseErr) {
                        console.warn('Could not parse server data, using current state');
                    }
                }
            } catch (fetchErr) {
                console.warn('Initial server fetch failed, continuing with current state');
            }

            if (serverData.length === 0 && cropsData.length > 0) {
                serverData = cropsData;
            }

            // 2. Merge current form into the latest server list
            const existingIndex = serverData.findIndex(item => String(item.year).trim() === String(form.year).trim());
            let updatedData;
            if (existingIndex !== -1) {
                updatedData = serverData.map((item, index) => 
                    index === existingIndex ? { ...form } : item
                );
            } else {
                updatedData = [...serverData, form].sort((a, b) => String(a.year).localeCompare(String(b.year), undefined, { numeric: true }));
            }

            // 3. Update UI and Local
            setCropsData(updatedData);
            localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedData));

            // 4. Save COMPLETE combined list back to server
            let serverOk = false;
            try {
                const response = await fetch(getApiUrl('api_crops.php?category=paddy'), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(updatedData)
                });
                if (response.ok) {
                    serverOk = true;
                }
            } catch (postErr) {
                console.error('Server sync error on POST:', postErr);
            }

            if (serverOk) {
                if (existingIndex !== -1) {
                    setMessage(`✅ ${form.year} දත්ත සාර්ථකව යාවත්කාලීන කරන ලදී!`);
                } else {
                    setMessage(`✅ ${form.year} දත්ත සාර්ථකව එක් කරන ලදී!`);
                }
            } else {
                setMessage(`✅ ${form.year} දත්ත සාර්ථකව සුරැකිණි (Local).`);
            }
            setForm({ year: '', area: '', production: '' });

        } catch (apiErr) {
            console.error('WEE Sync error:', apiErr);
            setMessage('⚠ සර්වර් දෝෂයකි, කරුණාකර නැවත උත්සාහ කරන්න.');
        } finally {
            if (onSubmitting) onSubmitting(false);
            setTimeout(() => setMessage(''), 4000);
        }
        return;
    };

    const handleDelete = async (year) => {
        if (!window.confirm(`ඔබට ${year} දත්ත මැකීමට අවශ්‍ය බව සැකද?`)) return;

        const updatedData = cropsData.filter(item => String(item.year).trim() !== String(year).trim());

        // Update UI
        setCropsData(updatedData);
        setMessage('⏳ දත්ත මකමින් පවතියි...');

        // Save Local
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedData));

        // Sync Server
        try {
            const response = await fetch(getApiUrl('api_crops.php?category=paddy'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updatedData)
            });

            if (response.ok) {
                setMessage('✅ දත්ත සාර්ථකව මකා දමන ලදී!');
            } else {
                setMessage('⚠ සර්වර් දත්ත මැකීම අසාර්ථක විය, නමුත් බ්‍රවුසරයෙන් මකා දමන ලදී.');
            }
        } catch (err) {
            console.error('Delete sync failed:', err);
            setMessage('⚠ සර්වර් දත්ත මැකීම අසාර්ථක විය, නමුත් බ්‍රවුසරයෙන් මකා දමන ලදී.');
        }

        setTimeout(() => setMessage(''), 4000);
    };

    const handleEdit = (item) => {
        setForm({ ...item });
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setMessage('📝 ඔබ දැන් පවතින දත්ත සංස්කරණය කරයි.');
        setTimeout(() => setMessage(''), 3000);
    };

    return (
        <div className="manager-container">
            <div className="form-wrapper">
                <div className="premium-card">
                    <div className="card-header">
                        <div className="icon-box">🌱</div>
                        <h3 className="section-title">නව දත්ත ඇතුළත් කර සුරකින්න</h3>
                        {loading && <p className="loading-text">සර්වර් එකෙන් දත්ත ලබා ගනිමින් පවතියි...</p>}
                    </div>

                    {message && (
                        <div className={`status-message ${message.includes('✅') ? 'success' : 'error'} ${message.includes('⏳') ? 'pending' : ''}`}>
                            {message}
                        </div>
                    )}

                    <form className="premium-form" onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); console.log('🛑 Form submission attempt blocked'); return false; }}>
                        <div className="form-group">
                            <label>කන්නය</label>
                            <input
                                type="text"
                                value={form.year}
                                onChange={(e) => setForm({ ...form, year: e.target.value })}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        console.log('🛑 Enter key blocked in form input');
                                    }
                                }}
                                placeholder="උදා: 2023/2024 මහා හෝ 2024 යල"
                                required
                                disabled={loading}
                            />
                        </div>

                        <div className="form-group">
                            <label>වගා කල වපසරිය (හෙක්)</label>
                            <input
                                type="number"
                                step="0.01"
                                value={form.area}
                                onChange={(e) => setForm({ ...form, area: e.target.value })}
                                placeholder="හෙක්ටයාර ප්‍රමාණය"
                                required
                                disabled={loading}
                            />
                        </div>

                        <div className="form-group">
                            <label>වී නිෂ්පාදනය(මෙ.ටො)</label>
                            <input
                                type="number"
                                step="0.01"
                                value={form.production}
                                onChange={(e) => setForm({ ...form, production: e.target.value })}
                                placeholder="මෙට්‍රික් ටොන් ප්‍රමාණය"
                                required
                                disabled={loading}
                            />
                        </div>

                        <button type="button" className="submit-btn primary" onClick={handleSubmit} disabled={loading}>
                            <span className="btn-icon">💾</span> දත්ත සුරකින්න
                        </button>
                    </form>

                    <div className="card-info">
                        <p>මෙහි ඇතුළත් කරන දත්ත "වී වගාව" පිටුවේ වගුව වෙත ස්වයංක්‍රීයව එක් වේ.</p>
                        <p style={{ marginTop: '8px', fontSize: '0.8rem', opacity: 0.7 }}>මෙය ස්ථිරවම සර්වර් එකෙහි සුරැකේ.</p>
                    </div>
                </div>

                {/* Existing Data Table */}
                <div className="premium-card list-card" style={{ marginTop: '30px' }}>
                    <div className="card-header">
                        <h3 className="section-title">පවතින දත්ත කළමනාකරණය</h3>
                    </div>

                    <div className="table-wrapper">
                        <table className="manage-table">
                            <thead>
                                <tr>
                                    <th>කන්නය</th>
                                    <th>වපසරිය</th>
                                    <th>නිෂ්පාදනය</th>
                                    <th>ක්‍රියාකාරකම්</th>
                                </tr>
                            </thead>
                            <tbody>
                                {cropsData.length === 0 ? (
                                    <tr>
                                        <td colSpan="4" style={{ textAlign: 'center', padding: '30px', color: '#999' }}>දත්ත නොමැත</td>
                                    </tr>
                                ) : (
                                    cropsData.map((item, idx) => (
                                        <tr key={idx}>
                                            <td data-label="කන්නය"><strong>{item.year}</strong></td>
                                            <td data-label="වපසරිය">{item.area}</td>
                                            <td data-label="නිෂ්පාදනය">{item.production}</td>
                                            <td data-label="ක්‍රියාකාරකම්">
                                                <div className="action-cell">
                                                    <button onClick={() => handleEdit(item)} className="icon-btn edit-btn" title="Edit">📝</button>
                                                    <button onClick={() => handleDelete(item.year)} className="icon-btn delete-btn" title="Delete">🗑️</button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <style>{`
                .manager-container {
                    padding: clamp(20px, 5vw, 60px) 20px;
                    display: flex;
                    justify-content: center;
                    align-items: flex-start;
                    min-height: 80vh;
                    font-family: 'Outfit', 'Inter', sans-serif;
                }

                .form-wrapper {
                    width: 100%;
                    max-width: 550px;
                    animation: fadeInUp 0.6s ease-out;
                }

                @keyframes fadeInUp {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                .premium-card {
                    background: #ffffff;
                    border-radius: 24px;
                    padding: 40px;
                    box-shadow: 0 20px 50px rgba(0,0,0,0.08);
                    border: 1px solid rgba(0,0,0,0.03);
                    position: relative;
                }

                .card-header {
                    text-align: center;
                    margin-bottom: 35px;
                }

                .icon-box {
                    width: 70px;
                    height: 70px;
                    background: #f0f9ff;
                    border-radius: 18px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 2.2rem;
                    margin: 0 auto 15px auto;
                    box-shadow: inset 0 0 0 1px rgba(0,0,0,0.02);
                }

                .loading-text {
                    font-size: 0.85rem;
                    color: #64748b;
                    margin-top: 8px;
                    font-weight: 500;
                }

                .section-title {
                    margin: 0;
                    color: #1a3353;
                    font-size: 1.25rem;
                    font-weight: 700;
                    letter-spacing: -0.01em;
                }

                .status-message {
                    padding: 16px;
                    border-radius: 12px;
                    margin-bottom: 25px;
                    text-align: center;
                    font-weight: 600;
                    font-size: 1rem;
                    transition: all 0.3s;
                }

                .status-message.pending {
                    background: #fefce8;
                    color: #854d0e;
                    border: 1px solid #fef08a;
                }

                .status-message.success {
                    background: #ecfdf5;
                    color: #059669;
                    border: 1px solid #d1fae5;
                }

                .status-message.error {
                    background: #fef2f2;
                    color: #dc2626;
                    border: 1px solid #fee2e2;
                }

                .premium-form .form-group {
                    margin-bottom: 25px;
                }

                .premium-form label {
                    display: block;
                    margin-bottom: 12px;
                    color: #4b5563;
                    font-weight: 600;
                    font-size: 0.95rem;
                    padding-left: 4px;
                }

                .premium-form input {
                    width: 100%;
                    padding: 16px 20px;
                    border: 2px solid #f3f4f6;
                    border-radius: 16px;
                    font-size: 1.05rem;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    box-sizing: border-box;
                    background: #f9fafb;
                    color: #1f2937;
                }

                .premium-form input:focus {
                    outline: none;
                    border-color: #10b981;
                    background: #ffffff;
                    box-shadow: 0 0 0 5px rgba(16, 185, 129, 0.1);
                    transform: translateY(-2px);
                }

                .premium-form input:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .submit-btn {
                    width: 100%;
                    padding: 18px;
                    border-radius: 16px;
                    font-weight: 700;
                    font-size: 1.15rem;
                    cursor: pointer;
                    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
                    border: none;
                    margin-top: 15px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;
                }

                .submit-btn.primary {
                    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                    color: white;
                    box-shadow: 0 10px 25px rgba(16, 185, 129, 0.3);
                }

                .submit-btn.primary:hover:not(:disabled) {
                    box-shadow: 0 15px 30px rgba(16, 185, 129, 0.4);
                    transform: translateY(-3px) scale(1.02);
                }

                .submit-btn:disabled {
                    background: #e5e7eb;
                    color: #9ca3af;
                    box-shadow: none;
                }

                .card-info {
                    margin-top: 30px;
                    padding-top: 25px;
                    border-top: 1px solid #f3f4f6;
                    text-align: center;
                }

                .card-info p {
                    color: #6b7280;
                    font-size: 0.9rem;
                    line-height: 1.5;
                    margin: 0;
                }

                @media (max-width: 640px) {
                    .premium-card {
                        padding: 25px;
                    }
                    .section-title {
                        font-size: 1.3rem;
                    }
                    .manager-container {
                        padding: 20px 10px;
                    }
                }

                .table-wrapper {
                    overflow-x: auto;
                    margin-top: 10px;
                    border-radius: 12px;
                }

                .manage-table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 0.95rem;
                }

                .manage-table th {
                    text-align: left;
                    padding: 15px;
                    background: #f8fafc;
                    color: #475569;
                    font-weight: 700;
                    border-bottom: 2px solid #f1f5f9;
                }

                .manage-table td {
                    padding: 15px;
                    border-bottom: 1px solid #f1f5f9;
                    color: #1e293b;
                }

                /* Mobile Table Transformation */
                @media (max-width: 600px) {
                    .manage-table thead {
                        display: none;
                    }
                    .manage-table tr {
                        display: block;
                        background: #f9fafb;
                        border-radius: 16px;
                        margin-bottom: 15px;
                        padding: 15px;
                        border: 1px solid #eee;
                    }
                    .manage-table td {
                        display: flex;
                        justify-content: space-between;
                        padding: 8px 0;
                        border: none;
                    }
                    .manage-table td::before {
                        content: attr(data-label);
                        font-weight: 700;
                        color: #64748b;
                        font-size: 0.85rem;
                    }
                    .action-cell {
                        justify-content: center;
                        border-top: 1px solid #eee;
                        padding-top: 12px !important;
                        margin-top: 8px;
                    }
                }

                .action-cell {
                    display: flex;
                    gap: 10px;
                }

                .icon-btn {
                    background: none;
                    border: none;
                    font-size: 1.2rem;
                    cursor: pointer;
                    padding: 8px;
                    border-radius: 8px;
                    transition: all 0.2s;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .edit-btn:hover {
                    background: #f0fdf4;
                    transform: scale(1.1);
                }

                .delete-btn:hover {
                    background: #fef2f2;
                    transform: scale(1.1);
                }

                .list-card {
                    padding: 30px !important;
                }
            `}</style>
        </div>
    );
}
